import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import type { ListQuery } from '../../core/types/api';
import type { CrudService } from '../../core/services/crud.service';

interface EntitySelectProps<T extends { id: number }> {
  /** Stable query cache key — share with other readers of the same list. */
  cacheKey: readonly unknown[];
  service: CrudService<T, Partial<T>, Partial<T>>;
  query?: ListQuery;
  /** Currently selected id. `null` / `undefined` means "no selection". */
  value: number | null | undefined;
  onChange: (id: number | null) => void;
  label: string;
  helperText?: string;
  /** How to render an option as plain text. Defaults to `(o as { name }).name`. */
  getOptionLabel?: (entity: T) => string;
  /** Optional id to omit (e.g. exclude self when picking a parent). */
  excludeId?: number;
  required?: boolean;
  /** Disable interaction while still showing the current selection. */
  disabled?: boolean;
}

/**
 * Generic Autocomplete that loads its options from a `CrudService` and lets
 * the user pick one by name. Backed by TanStack Query so multiple selects
 * for the same resource share a single fetch via the same cache key.
 *
 * Used for picking the FK target on forms — Category, Persona, Stage,
 * Brand, etc. — without making the user paste a numeric id.
 */
export function EntitySelect<T extends { id: number; name?: string }>({
  cacheKey,
  service,
  query,
  value,
  onChange,
  label,
  helperText,
  getOptionLabel,
  excludeId,
  required,
  disabled,
}: EntitySelectProps<T>) {
  const list = useQuery({
    queryKey: cacheKey,
    queryFn: () => service.list({ pageSize: 200, sort: 'name', ...query }),
  });

  const options = (list.data?.data ?? []).filter((o) => o.id !== excludeId);
  const selected = options.find((o) => o.id === value) ?? null;
  const labelFor = getOptionLabel ?? ((o: T) => o.name ?? `#${o.id}`);

  return (
    <Autocomplete<T, false, false, false>
      // `fullWidth` makes the control expand to fill its container so it
      // plays nicely inside <Stack direction="row">. Without it MUI sizes
      // the field to its content and the dropdowns end up squashed.
      fullWidth
      disabled={disabled}
      options={options}
      value={selected}
      onChange={(_, next) => onChange(next?.id ?? null)}
      getOptionLabel={labelFor}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      loading={list.isLoading}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={helperText}
          // The visible asterisk from `required` on TextField is cosmetic;
          // to actually block form submission we forward `required` to the
          // hidden Autocomplete input so the browser's :invalid state
          // fires when no option is selected.
          inputProps={{
            ...params.inputProps,
            required: required && value == null,
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {list.isLoading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
